import { useMemo } from 'react';
import { Button, Flex, Modal, Radio, TextInput } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useGetForm, usePatchForm } from 'api/forms';
import { useShowNotification } from 'hooks';
import type { Form as FormType } from 'types';

import { useFormEditContext } from '../../context';
import { RadioBox } from '../../../FormsList/NewFormWizard/components/RadioBox';

type Props = {
  opened: boolean;
  onClose: () => void;
};

type FormData = Pick<FormType, 'name' | 'signType'> & { signType: any };

export const GeneralSettingsModal = ({ opened, onClose }: Props) => {
  const {
    form: { id, name, signType },
  } = useFormEditContext();
  const {
    watch,
    register,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: { name, signType },
  });
  const [nameValue, signTypeValue] = watch(['name', 'signType']);
  const isDirty = useMemo(
    () => nameValue !== name || signTypeValue !== signType,
    [name, signType, nameValue, signTypeValue],
  );
  const { mutateAsync: patchForm } = usePatchForm({ id });
  const { refetch: refetchForm } = useGetForm({ id });
  const showNotification = useShowNotification();
  const { t } = useTranslation('forms');

  const onSubmit = async (data: FormData) => {
    try {
      await patchForm(data);
      showNotification({
        message: t('editView.sidebar.generalSettings.updateSuccessToast'),
        variant: 'success',
      });
      onClose();
      refetchForm();
    } catch (error) {
      showNotification({
        message: t('editView.sidebar.generalSettings.updateErrorToast'),
        variant: 'error',
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('editView.sidebar.generalSettings.modal.title')}
      size="md"
      centered
    >
      <Flex direction="column" gap="lg">
        <TextInput
          {...register('name')}
          label={t('editView.sidebar.generalSettings.modal.nameField.label')}
        />
        <Radio.Group
          value={signTypeValue}
          onChange={value => {
            setValue('signType', z.enum(['SMS', 'ANY']).parse(value));
          }}
          label={t(
            'editView.sidebar.generalSettings.modal.signTypeField.label',
          )}
          required
          withAsterisk={false}
        >
          <RadioBox
            value="ANY"
            checked={signTypeValue === 'ANY'}
            label={t(
              'editView.sidebar.generalSettings.modal.signTypeField.radioOptionAny.label',
            )}
            description={t(
              'editView.sidebar.generalSettings.modal.signTypeField.radioOptionAny.description',
            )}
          />
          <RadioBox
            value="SMS"
            checked={signTypeValue === 'SMS'}
            label={t(
              'editView.sidebar.generalSettings.modal.signTypeField.radioOptionSms.label',
            )}
            description={t(
              'editView.sidebar.generalSettings.modal.signTypeField.radioOptionSms.description',
            )}
          />
        </Radio.Group>
        <Flex justify="flex-end" gap="md">
          <Button variant="default" onClick={onClose}>
            {t('editView.sidebar.generalSettings.modal.cancelButton')}
          </Button>
          <Button
            disabled={!isDirty}
            onClick={handleSubmit(onSubmit)}
            loading={isSubmitting}
          >
            {t('editView.sidebar.generalSettings.modal.saveButton')}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
